import { useState } from 'react';
import { DownloadSimpleIcon as DownloadIcon } from '@phosphor-icons/react';

import { Breadcrumb } from 'components/Breadcrumb';
import { LinkButton } from 'components/button/ButtonLink';
import { PageHeader } from 'components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import { TimeFrame } from 'components/time-frame/time-frame';
import { TimeFrameState } from 'components/time-frame/types';
import { useTranslation } from 'contexts/translation-context';
import useSWR from 'swr';
import { ErrorState } from 'components/error-state/ErrorState';
import {
  SustainabilityAveragesGrid,
  SustainabilityTotalsGrid,
} from 'src/app/sustainability/components/SustainabilityMetricCard';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { fetchCustomerPortalSustainabilityReporting } from './models';

export const SustainabilityReportPage = () => {
  const { i18n } = useTranslation();
  const [timeFrame, setTimeFrame] = useState<null | TimeFrameState>(null);
  const { customer } = useCustomerPortalCustomer();
  const { data, error, mutate } = useSWR(
    timeFrame
      ? ['customer-portal-sustainability-reporting', customer.id, timeFrame.startDate, timeFrame.endDate]
      : null,
    () =>
      fetchCustomerPortalSustainabilityReporting({
        customerId: customer.id,
        startDate: timeFrame?.startDate || '',
        endDate: timeFrame?.endDate || '',
      }),
  );

  const results = data ?? {
    totalOrders: 0,
    totalDistance: 0,
    totalWeight: 0,
    totalEmissions: 0,
  };

  const title = i18n('customerPortal.sustainability.sustainability');
  return (
    <div>
      <PageHeader title={title} />

      <PageHeading
        leftSide={<Breadcrumb currentItem={title} />}
        rightSide={
          <LinkButton
            to="https://storage.googleapis.com/transdirect-static-files/transdirect-esg-one-pager-v1.pdf"
            target="_blank"
          >
            {i18n('customerPortal.sustainability.policy')}
          </LinkButton>
        }
      />

      <div className="flex justify-between items-end px-8">
        <div>
          <LinkButton
            to={`/api/customer-portal/sustainability-report.pdf?customerId=${customer.id}&startDate=${timeFrame?.startDate}&endDate=${timeFrame?.endDate}`}
            target="_blank"
            iconLeft={<DownloadIcon className="w-4 h-4 mr-2" />}
          >
            {i18n('download.download')}
          </LinkButton>
        </div>

        <div>
          <TimeFrame onChange={setTimeFrame} />
        </div>
      </div>

      <div className="p-8">
        {error ? (
          <ErrorState error={error} onRetry={() => mutate()} variant="section" />
        ) : (
          <>
            <div className="heading-three mb-4">{i18n('customerPortal.sustainability.total')}</div>

            <SustainabilityTotalsGrid results={results} className="mb-8" />

            <div className="heading-three mb-4">{i18n('customerPortal.sustainability.avgPerOrder')}</div>

            <SustainabilityAveragesGrid results={results} />
          </>
        )}
      </div>
    </div>
  );
};
